import { useState } from "react";
import { Download, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { downloadFile, formatApiError } from "@/lib/api";
import { useApp } from "@/lib/i18n";
import { plainText } from "./transcriptUtils";

const FORMATS = [["docx", "DOCX (Word)"], ["pdf", "PDF"], ["txt", "TXT"], ["srt", "SRT (sari kata)"], ["vtt", "VTT (sari kata web)"]];

const Pick = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10 rounded-xl" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export const CopyButton = ({ segs, sections, names, opts }) => {
  const { L } = useApp();
  const copy = async () => {
    await navigator.clipboard.writeText(plainText(segs, sections, names, opts));
    toast.success(L("Transkrip disalin ke papan klip", "Transcript copied to clipboard"));
  };
  return <Button variant="outline" size="sm" className="rounded-xl" onClick={copy} data-testid="transcript-copy-btn"><Copy size={14} className="mr-1" /> {L("Salin", "Copy")}</Button>;
};

export const ExportDialog = ({ meeting, versions, defaultVersion }) => {
  const { L } = useApp();
  const [o, setO] = useState({ fmt: "docx", version: defaultVersion, timestamps: true, speakers: true, layout: "paragraph" });
  const set = (k, v) => setO((x) => ({ ...x, [k]: v }));
  const go = async () => {
    const q = new URLSearchParams({ version: o.version, timestamps: o.timestamps, speakers: o.speakers, layout: o.layout });
    try {
      await downloadFile(`/meetings/${meeting.id}/export/transcript/${o.fmt}?${q}`, `${meeting.title}-transkrip.${o.fmt}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const sub = ["srt", "vtt"].includes(o.fmt);
  return (
    <Dialog onOpenChange={(v) => v && set("version", defaultVersion)}>
      <DialogTrigger asChild><Button size="sm" className="rounded-xl bg-violet-600 hover:bg-violet-700" data-testid="transcript-export-btn"><Download size={14} className="mr-1" /> {L("Eksport", "Export")}</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{L("Eksport transkrip", "Export transcript")}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5"><Label className="text-xs">{L("Format", "Format")}</Label><Pick value={o.fmt} onChange={(v) => set("fmt", v)} options={FORMATS} testId="export-format-select" /></div>
          <div className="space-y-1.5"><Label className="text-xs">{L("Versi teks", "Text version")}</Label><Pick value={o.version} onChange={(v) => set("version", v)} options={versions} testId="export-version-select" /></div>
          {!sub && <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">{L("Susun atur", "Layout")}</Label><Pick value={o.layout} onChange={(v) => set("layout", v)} testId="export-layout-select" options={[["paragraph", L("Perenggan ikut pembicara + topik", "Paragraphs by speaker + topics")], ["segment", L("Setiap segmen", "Every segment")]]} /></div>}
          {!sub && <label className="flex items-center gap-2 text-sm"><Checkbox checked={o.timestamps} onCheckedChange={(v) => set("timestamps", !!v)} data-testid="export-timestamps-checkbox" /> {L("Cap masa", "Timestamps")}</label>}
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={o.speakers} onCheckedChange={(v) => set("speakers", !!v)} data-testid="export-speakers-checkbox" /> {L("Nama pembicara", "Speaker names")}</label>
        </div>
        <DialogFooter><Button onClick={go} className="rounded-xl bg-violet-600 hover:bg-violet-700" data-testid="export-download-btn"><Download size={14} className="mr-1" /> {L("Muat turun", "Download")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
